import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { getRegistrations, getRegistrationStats, updateRegistration, deleteRegistration } from "@/lib/api";
import { 
  Users, Search, Eye, Trash2, Mail, 
  UserCheck, ClipboardList, TrendingUp, Clock, CheckCircle, MessageSquare, Download
} from "lucide-react";
import { FormLabel } from "@/components/ui/form";

const TEAM_LABELS = {
  U13: "U13",
  U15: "U15",
  U17: "U17",
  C: "C",
  JONG: "Jong FTG",
  EERSTE: "Eerste ploeg",
};

const STATUS_OPTIONS = [
  { value: "pending", label: "In behandeling", color: "bg-yellow-500" },
  { value: "contacted", label: "Gecontacteerd", color: "bg-blue-500" },
  { value: "accepted", label: "Aanvaard", color: "bg-green-500" },
  { value: "rejected", label: "Afgewezen", color: "bg-red-500" },
];

export default function AdminPlayerRegistrations() {
  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    team: "",
    status: "",
    player_type: "",
    search: "",
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.status && filters.status !== "all") params.status = filters.status;
      
      const [regsResponse, statsResponse] = await Promise.all([
        getRegistrations(params),
        getRegistrationStats(),
      ]);
      
      let regs = regsResponse.data;

      if (filters.team && filters.team !== "all") {
        regs = regs.filter((registration) => registration.team_preference === filters.team);
      }

      if (filters.player_type && filters.player_type !== "all") {
        regs = regs.filter((registration) => registration.player_type === filters.player_type);
      }
      
      // Client-side search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        regs = regs.filter(r => 
          r.first_name?.toLowerCase().includes(searchLower) ||
          r.last_name?.toLowerCase().includes(searchLower) ||
          r.email?.toLowerCase().includes(searchLower)
        );
      }
      
      setRegistrations(regs);
      setStats(statsResponse.data);
    } catch (error) {
      console.error("Error fetching registrations:", error);
      toast.error("Kon registraties niet laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filters.team, filters.status, filters.player_type]);

  const handleSearch = () => {
    fetchData();
  };

  const handleStatusChange = async (registrationId, newStatus) => {
    try {
      await updateRegistration(registrationId, { status: newStatus });
      toast.success(`Status gewijzigd naar ${STATUS_OPTIONS.find(s => s.value === newStatus)?.label}`);
      fetchData();
      if (selectedRegistration?.id === registrationId) {
        setSelectedRegistration(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      toast.error("Kon status niet bijwerken");
    }
  };

  const handleNotesUpdate = async (registrationId, notes) => {
    try {
      await updateRegistration(registrationId, { admin_notes: notes });
      toast.success("Notities opgeslagen");
    } catch (error) {
      toast.error("Kon notities niet opslaan");
    }
  };

  const handleDelete = async (registrationId) => {
    if (!confirm("Weet je zeker dat je deze registratie wilt verwijderen?")) return;
    
    try {
      await deleteRegistration(registrationId);
      toast.success("Registratie verwijderd");
      fetchData();
      setShowDetailDialog(false);
    } catch (error) {
      toast.error("Kon registratie niet verwijderen");
    }
  };

  const openDetail = (registration) => {
    setSelectedRegistration(registration);
    setShowDetailDialog(true);
  };

  const getStatusBadge = (status) => {
    const option = STATUS_OPTIONS.find(s => s.value === status);
    return (
      <Badge className={`${option?.color || 'bg-gray-500'} text-white`}>
        {option?.label || status}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("nl-BE");
    } catch {
      return dateStr;
    }
  };

  const exportToCSV = () => {
    const headers = ["Voornaam", "Achternaam", "Email", "Telefoon", "Geboortedatum", "Type", "Team Voorkeur", "Status", "Opmerkingen", "Datum"];
    const csvData = registrations.map(reg => [
      reg.first_name,
      reg.last_name,
      reg.email,
      reg.phone || "",
      reg.date_of_birth || "",
      reg.player_type === 'competitief' ? 'Competitief' : 'Recreatief',
      TEAM_LABELS[reg.team_preference] || reg.team_preference,
      STATUS_OPTIONS.find(s => s.value === reg.status)?.label || reg.status,
      reg.remarks || "",
      formatDate(reg.created_at)
    ]);

    const csvContent = [
      headers.join(","),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `speler_inschrijvingen_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    toast.success("CSV geëxporteerd");
  };

  return (
    <div className="p-6" data-testid="admin-player-registrations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-50 rounded-xl">
            <Users className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-slate-900 font-['Chivo'] tracking-tight">
              Speler Inschrijvingen
            </h1>
            <p className="text-slate-500 font-['Manrope'] mt-0.5">
              Beheer speler kandidaturen voor teams
            </p>
          </div>
        </div>
        <Button
          onClick={exportToCSV}
          variant="outline"
          disabled={registrations.length === 0}
          className="border-slate-200"
          data-testid="export-csv-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          Exporteer CSV
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {stats.total}
                  </p>
                  <p className="text-sm text-slate-500">Totaal</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-yellow-100">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {stats.by_status?.pending || 0}
                  </p>
                  <p className="text-sm text-slate-500">In behandeling</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {stats.by_status?.accepted || 0}
                  </p>
                  <p className="text-sm text-slate-500">Aanvaard</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-100">
                  <TrendingUp className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {stats.by_type?.competitief || 0}
                  </p>
                  <p className="text-sm text-slate-500">Competitief</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="mb-6 bg-white border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-slate-700">Zoeken</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Naam of email..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="bg-white border-slate-200"
                  data-testid="search-input"
                />
                <Button onClick={handleSearch} variant="outline" className="border-slate-200">
                  <Search className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="w-40">
              <Label className="text-slate-700">Team</Label>
              <Select
                value={filters.team}
                onValueChange={(value) => setFilters(prev => ({ ...prev, team: value }))}
              >
                <SelectTrigger className="bg-white border-slate-200" data-testid="team-filter">
                  <SelectValue placeholder="Alle teams" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="all">Alle teams</SelectItem>
                  {Object.entries(TEAM_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-40">
              <Label className="text-slate-700">Status</Label>
              <Select
                value={filters.status}
                onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger className="bg-white border-slate-200" data-testid="status-filter">
                  <SelectValue placeholder="Alle statussen" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="all">Alle statussen</SelectItem>
                  {STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-40">
              <Label className="text-slate-700">Type</Label>
              <Select
                value={filters.player_type}
                onValueChange={(value) => setFilters(prev => ({ ...prev, player_type: value }))}
              >
                <SelectTrigger className="bg-white border-slate-200" data-testid="type-filter">
                  <SelectValue placeholder="Alle types" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="all">Alle types</SelectItem>
                  <SelectItem value="competitief">Competitief</SelectItem>
                  <SelectItem value="recreatief">Recreatief</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button 
              variant="ghost" 
              onClick={() => setFilters({ team: "", status: "", player_type: "", search: "" })}
              className="text-slate-500 hover:text-slate-700"
            >
              Reset
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Registrations Table */}
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-900">
            <ClipboardList className="w-5 h-5" />
            Kandidaturen ({registrations.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <p className="text-slate-500">Laden...</p>
            </div>
          ) : registrations.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">
                Geen registraties gevonden
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left p-3 font-medium text-slate-600">Naam</th>
                    <th className="text-left p-3 font-medium text-slate-600">Team</th>
                    <th className="text-left p-3 font-medium text-slate-600">Type</th>
                    <th className="text-left p-3 font-medium text-slate-600">Datum</th>
                    <th className="text-left p-3 font-medium text-slate-600">Status</th>
                    <th className="text-right p-3 font-medium text-slate-600">Acties</th>
                  </tr>
                </thead>
                <tbody>
                  {registrations.map((reg) => (
                    <tr 
                      key={reg.id} 
                      className="border-b border-slate-100 hover:bg-slate-50"
                      data-testid={`registration-row-${reg.id}`}
                    >
                      <td className="p-3">
                        <div>
                          <p className="font-medium text-slate-900">
                            {reg.first_name} {reg.last_name}
                          </p>
                          <p className="text-sm text-slate-500">
                            {reg.email}
                          </p>
                        </div>
                      </td>
                      <td className="p-3">
                        <Badge variant="outline" className="border-amber-500 text-amber-700">
                          {TEAM_LABELS[reg.team_preference] || reg.team_preference}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <span className="text-sm text-slate-700">
                          {reg.player_type === 'competitief' ? 'Competitief' : 'Recreatief'}
                        </span>
                      </td>
                      <td className="p-3 text-sm text-slate-500">
                        {formatDate(reg.created_at)}
                      </td>
                      <td className="p-3">
                        {getStatusBadge(reg.status)}
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openDetail(reg)}
                            className="hover:bg-slate-100"
                            data-testid={`view-registration-${reg.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(reg.id)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            data-testid={`delete-registration-${reg.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900">
              Registratie Details
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              {selectedRegistration?.first_name} {selectedRegistration?.last_name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedRegistration && (
            <div className="space-y-6">
              {/* Status Update */}
              <div className="flex items-center gap-4">
                <Label className="text-slate-700">Status:</Label>
                <Select
                  value={selectedRegistration.status}
                  onValueChange={(value) => handleStatusChange(selectedRegistration.id, value)}
                >
                  <SelectTrigger className="w-48 bg-white border-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    {STATUS_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Info Grid */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="p-4 rounded-lg bg-slate-50">
                  <h4 className="font-medium mb-3 flex items-center gap-2 text-slate-900">
                    <UserCheck className="w-4 h-4" />
                    Spelergegevens
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Type:</span>
                      <span className="text-slate-900">
                        {selectedRegistration.player_type === 'competitief' ? 'Competitief' : 'Recreatief'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Team voorkeur:</span>
                      <Badge variant="outline" className="border-amber-500 text-amber-700">
                        {TEAM_LABELS[selectedRegistration.team_preference]}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Geboortedatum:</span>
                      <span className="text-slate-900">{selectedRegistration.date_of_birth}</span>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-slate-50">
                  <h4 className="font-medium mb-3 flex items-center gap-2 text-slate-900">
                    <Mail className="w-4 h-4" />
                    Contactgegevens
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">Email:</span>
                      <a href={`mailto:${selectedRegistration.email}`} className="text-blue-600 hover:underline">
                        {selectedRegistration.email}
                      </a>
                    </div>
                    {selectedRegistration.additional_email && (
                      <div className="flex justify-between items-center">
                        <span className="text-slate-500">Extra email:</span>
                        <a href={`mailto:${selectedRegistration.additional_email}`} className="text-blue-600 hover:underline">
                          {selectedRegistration.additional_email}
                        </a>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">Telefoon:</span>
                      <a href={`tel:${selectedRegistration.phone}`} className="text-blue-600 hover:underline">
                        {selectedRegistration.phone}
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Remarks */}
              {selectedRegistration.remarks && (
                <div className="p-4 rounded-lg bg-slate-50">
                  <h4 className="font-medium mb-2 flex items-center gap-2 text-slate-900">
                    <MessageSquare className="w-4 h-4" />
                    Opmerkingen van kandidaat
                  </h4>
                  <p className="text-sm text-slate-600">
                    {selectedRegistration.remarks}
                  </p>
                </div>
              )}

              {/* Admin Notes */}
              <div>
                <Label className="mb-2 block text-slate-700">
                  Admin notities
                </Label>
                <Textarea
                  defaultValue={selectedRegistration.admin_notes || ""}
                  onBlur={(e) => handleNotesUpdate(selectedRegistration.id, e.target.value)}
                  placeholder="Voeg interne notities toe..."
                  rows={3}
                  className="bg-white border-slate-200"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-between pt-4 border-t border-slate-200">
                <Button
                  variant="destructive"
                  onClick={() => handleDelete(selectedRegistration.id)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Verwijderen
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowDetailDialog(false)}
                  className="border-slate-200"
                >
                  Sluiten
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
